import type {
  ContentfulHeadlineBlock,
  ContentfulTextBlock,
} from "@scott/contentful/types";
import type { TextSectionProps } from "@/scott/ui/blocks/text-section/text-section";
import { getBlockId } from "@/services/contentful/assemblies/page/adapter/utils";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type { SectionTextPageBlock, UiConfiguration } from "@/types/page-block";

export interface SectionTextInput {
  sys?: { id: string };
  __typename: "SectionText";
  internalName: string;
  containerWidth: "narrow" | "medium" | "wide";
  textColumns?: TextSectionProps["textColumns"];
  textVariant: "xlarge" | "large" | "normal" | "small";
  headline?: ContentfulHeadlineBlock;
  text?: ContentfulTextBlock;
  headlineVariant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  headlineDash?: boolean;
  anchorId?: string;
  uiConfiguration?: UiConfigurationInput;
}

const columnWidthMap = {
  narrow: 8,
  medium: 10,
  wide: 12,
} as const satisfies Record<
  SectionTextInput["containerWidth"],
  NonNullable<TextSectionProps["columnWidth"]>
>;

const textVariantMap = {
  normal: "p",
  large: "p-large",
  xlarge: "p-xlarge",
  small: "p-small",
} as const satisfies Record<
  NonNullable<SectionTextInput["textVariant"]>,
  NonNullable<TextSectionProps["textVariant"]>
>;

export function SectionTextAdapter(
  item: SectionTextInput,
  index: number
): SectionTextPageBlock {
  const uiConfig: UiConfiguration | undefined = item.uiConfiguration
    ? UiConfigurationAdapter(item.uiConfiguration)
    : undefined;

  return {
    sys: item.sys,
    id: getBlockId(item, index),
    __typename: item.__typename,
    internalName: item.internalName,
    text: item.text,
    headline: item.headline,
    headlineDash: Boolean(item.headlineDash),
    headlineVariant: item.headlineVariant,
    textVariant: textVariantMap[item.textVariant] ?? "p",
    columnWidth: columnWidthMap[item.containerWidth] ?? 10,
    textColumns: item.textColumns,
    anchorId: item.anchorId,
    backgroundColor: uiConfig?.backgroundColor,
    headlineColor: uiConfig?.headlineColor,
    color: uiConfig?.textColor,
    paddingTop: uiConfig?.paddingTop,
    paddingBottom: uiConfig?.paddingBottom,
  };
}
